import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import ClinicProfileForm from "../IntakeForms/ClinicProfileForm";
import BusinessHoursForm from "../IntakeForms/BusinessHoursForm";
import StaffForm from "../IntakeForms/StaffForm";
import ServicesForm from "../IntakeForms/ServicesForm";
import PaymentPolicyForm from "../IntakeForms/PaymentPolicyForm";
import BookingSystemForm from "../IntakeForms/BookingSystemForm";
import ToneVoiceForm from "../IntakeForms/ToneVoiceForm";
import UploadDocumentsForm from "../IntakeForms/UploadDocumentsForm";
import "../styles/intake.css";

function convertToAMPM(timeStr) {
  if (!timeStr || timeStr.includes("AM") || timeStr.includes("PM")) return timeStr;
  const [hourStr, minute] = timeStr.split(":");
  let hour = parseInt(hourStr, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  if (hour === 0) hour = 12;
  return `${hour}:${minute} ${ampm}`;
}

export default function IntakePage() {
  const [formData, setFormData] = useState({});
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [valid, setValid] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const isFormReady = formData.agreement1 && formData.agreement2;

  useEffect(() => {
    if (token) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/validate-token?token=${token}`)
        .then((res) => {
          if (!res.ok) throw new Error("Invalid token");
          return res.json();
        })
        .then(async (data) => {
          setValid(true);
          const clinic = data.clinic;

          let savedFormData = {};
          try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-intake-form?clinic_id=${clinic.id}`);
            const intakeRes = await res.json();
            if (intakeRes.found) {
              savedFormData = intakeRes.form_data || {};
            }
          } catch (err) {
            console.warn("⚠️ Failed to fetch saved intake form:", err);
          }

          const convertedBusinessHours = clinic.business_hours
            ? Object.fromEntries(
                Object.entries(clinic.business_hours).map(([day, hours]) => [
                  day,
                  {
                    open: convertToAMPM(hours.open),
                    close: convertToAMPM(hours.close),
                    closed: hours.closed ?? false,
                  },
                ])
              )
            : {};

          setFormData((prev) => ({
            ...prev,
            ...savedFormData,
            clinic_id: clinic.id,
            clinic_name: clinic.name || "",
            website: clinic.website || "",
            address: clinic.address || "",
            admin: clinic.admin || "",
            phone: clinic.phone || "",
            email: clinic.email || "",
            instagram: clinic.instagram || "",
            facebook: clinic.facebook || "",
            twitter: clinic.twitter || "",
            google_map: clinic.google_map || "",
            agreement1: clinic.agreement1 ?? false,
            agreement2: clinic.agreement2 ?? false,
            business_license: Array.isArray(clinic.business_license) ? clinic.business_license : [],
            documents: Array.isArray(clinic.documents) ? clinic.documents : [],
            business_hours: convertedBusinessHours,
            walk_in_allowed: clinic.walk_in_allowed ?? null,
          }));
        })
        .catch(() => setValid(false));
    }
  }, [token]);

  const handleSubmit = async () => {
    const payload = {
        token,
        training_data: formData,
      };
      
      //   console.log("Submitting payload:", payload);
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/submit-intake`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (res.ok) setSubmitted(true);
  };

  if (valid === false) {
    return (
      <div className="intake-container">
        <h2 className="error-text">❌ Invalid or expired token.</h2>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="intake-container">
        <h2 className="success-text">✅ Thanks! Your setup is in progress.</h2>
      </div>
    );
  }
  

  return (
    <div className="intake-container">
      <ClinicProfileForm formData={formData} setFormData={setFormData} />
      <BusinessHoursForm formData={formData} setFormData={setFormData} />
      <StaffForm formData={formData} setFormData={setFormData} />
      <ServicesForm formData={formData} setFormData={setFormData} />
      <PaymentPolicyForm formData={formData} setFormData={setFormData} clinicId={formData.clinic_id} />
      <BookingSystemForm formData={formData} setFormData={setFormData} clinicId={formData.clinic_id} />
      <ToneVoiceForm formData={formData} setFormData={setFormData} clinicId={formData.clinic_id} />
      <UploadDocumentsForm formData={formData} setFormData={setFormData} />

      <div className="separator" style={{ marginTop: "100px", height: "4px", margin: "0 auto 32px auto" }} />

      {/* Agreement Checkboxes */}
      <div className="agreement-section">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.agreement1}
            onChange={(e) => setFormData({ ...formData, agreement1: e.target.checked })}
          />
          By submitting this form, I agree to allow Olumis AI to use the above information—including service details, care
          instructions, staff data, and uploaded materials—for the purpose of training and configuring my clinic’s AI
          assistant.
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.agreement2}
            onChange={(e) => setFormData({ ...formData, agreement2: e.target.checked })}
          />
          I acknowledge that this information will not be shared outside the scope of my clinic’s assistant and will be
          handled securely.
        </label>
      </div>

      <div className="note-section">
        <p style={{ marginTop: "8px", fontStyle: "italic", color: "#555" }}>
          ⚠️ Please make sure you have clicked the <strong>Save</strong> button in each section before submitting.
        </p>
      </div>

      <div className="submit-section">
        <button
          className="blue-button"
          disabled={!isFormReady}
          onClick={() => {
            const confirmSubmit = window.confirm(
              "Are you sure you have saved the information in each section and are ready to submit?"
            );
            if (confirmSubmit) handleSubmit();
          }}
        >
          Submit
        </button>
      </div>
    </div>
  );
}
